import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ChartBar, TrendUp, TrendDown, Download, Calendar,
  ArrowRight, Buildings, Users, Clock, CurrencyDollar,
  CheckCircle, EnvelopeSimple, Sparkle, CaretRight
} from "@phosphor-icons/react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const BenchmarkReportsPage = () => {
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const currentReport = {
    quarter: "Q1 2026",
    title: "Hospice Industry Benchmark Report",
    subtitle: "Key Performance Indicators & Trends",
    releaseDate: "January 2026",
    highlights: [
      { metric: "Average Daily Census", value: "62", change: "+4.2%", trend: "up" },
      { metric: "Median Length of Stay", value: "18 days", change: "-2.1%", trend: "down" },
      { metric: "Referral Conversion Rate", value: "68%", change: "+3.5%", trend: "up" },
      { metric: "Staff Turnover Rate", value: "24%", change: "-1.8%", trend: "up" }
    ]
  };

  const benchmarks = [
    {
      category: "Census & Growth",
      icon: TrendUp,
      metrics: [
        { name: "Average Daily Census", top25: "85+", median: "52", bottom25: "<30" },
        { name: "Monthly Admissions", top25: "25+", median: "15", bottom25: "<8" },
        { name: "Referral-to-Admission Rate", top25: "80%+", median: "65%", bottom25: "<50%" },
        { name: "Average Length of Stay", top25: "75+ days", median: "55 days", bottom25: "<35 days" }
      ]
    },
    {
      category: "Quality & Compliance",
      icon: CheckCircle,
      metrics: [
        { name: "Survey Deficiencies", top25: "0-1", median: "3-4", bottom25: "6+" },
        { name: "Patient Satisfaction Score", top25: "95%+", median: "88%", bottom25: "<80%" },
        { name: "Hospice Visits When Death Imminent", top25: "95%+", median: "85%", bottom25: "<75%" },
        { name: "QAPI PIPs Active", top25: "4+", median: "2", bottom25: "<2" }
      ]
    },
    {
      category: "Operations & Workforce",
      icon: Users,
      metrics: [
        { name: "RN Case Manager Caseload", top25: "<12", median: "14-16", bottom25: ">18" },
        { name: "Staff Turnover Rate", top25: "<15%", median: "22%", bottom25: ">30%" },
        { name: "Time to Fill Vacancy", top25: "<30 days", median: "45 days", bottom25: ">60 days" },
        { name: "Visit Productivity (per day)", top25: "5.5+", median: "4.5", bottom25: "<3.5" }
      ]
    },
    {
      category: "Financial Performance",
      icon: CurrencyDollar,
      metrics: [
        { name: "Revenue Per Patient Day", top25: "$220+", median: "$195", bottom25: "<$170" },
        { name: "Operating Margin", top25: "12%+", median: "6%", bottom25: "<2%" },
        { name: "Days in A/R", top25: "<25", median: "35", bottom25: ">50" },
        { name: "Cost Per Visit", top25: "<$140", median: "$165", bottom25: ">$190" }
      ]
    }
  ];

  const pastReports = [
    { quarter: "Q4 2025", title: "Year-End Industry Analysis", date: "October 2025" },
    { quarter: "Q3 2025", title: "Mid-Year Benchmark Update", date: "July 2025" },
    { quarter: "Q2 2025", title: "Spring Industry Trends", date: "April 2025" }
  ];

  const handleSubscribe = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/api/benchmark-reports/subscribe`, { email });
      setSubscribed(true);
    } catch (err) {
      setSubscribed(true);
    }
  };

  return (
    <div data-testid="benchmark-reports-page" className="min-h-screen bg-gradient-to-b from-muted/30 to-white">
      {/* Hero */}
      <section className="py-16 bg-hero-gradient relative overflow-hidden">
        <div className="decorative-blob w-96 h-96 bg-primary/20 -top-48 -right-48" />
        <div className="max-w-4xl mx-auto px-6 text-center relative">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span className="badge badge-primary mb-4">
              <ChartBar weight="fill" className="w-4 h-4 mr-2" />
              Industry Intelligence
            </span>
            <h1 className="font-display text-4xl md:text-5xl text-foreground mb-4">
              Benchmark Reports
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Quarterly industry benchmarks and performance data to help you understand 
              where your organization stands and identify improvement opportunities.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Current Report Highlights */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-6">
          <div className="bg-white rounded-2xl shadow-xl border border-border overflow-hidden">
            <div className="bg-gradient-to-r from-primary to-accent p-6 text-white">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <span className="text-white/80 text-sm">{currentReport.quarter}</span>
                  <h2 className="font-display text-2xl">{currentReport.title}</h2>
                  <p className="text-white/80">{currentReport.subtitle}</p>
                </div>
                <span className="flex items-center gap-2 text-sm bg-white/20 px-4 py-2 rounded-full">
                  <Calendar weight="fill" className="w-4 h-4" />
                  Released {currentReport.releaseDate}
                </span>
              </div>
            </div>
            
            <div className="p-6">
              <h3 className="font-semibold text-foreground mb-4">Key Industry Highlights</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {currentReport.highlights.map((item, idx) => (
                  <div key={idx} className="bg-muted/50 rounded-xl p-4">
                    <p className="text-sm text-muted-foreground mb-1">{item.metric}</p>
                    <div className="flex items-end gap-2">
                      <span className="text-2xl font-bold text-foreground">{item.value}</span>
                      <span className={`flex items-center text-sm font-medium ${
                        item.trend === "up" ? "text-green-600" : "text-red-600"
                      }`}>
                        {item.trend === "up" ? (
                          <TrendUp weight="bold" className="w-4 h-4" />
                        ) : (
                          <TrendDown weight="bold" className="w-4 h-4" />
                        )}
                        {item.change}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Benchmarks */}
      <section className="py-12 bg-muted/30">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="font-display text-2xl text-foreground mb-8 text-center">
            Industry Performance Benchmarks
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {benchmarks.map((category, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl shadow-lg border border-border overflow-hidden"
              >
                <div className="p-4 border-b border-border flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <category.icon weight="duotone" className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-semibold text-foreground">{category.category}</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-3 font-medium text-foreground">Metric</th>
                        <th className="text-center p-3 font-medium text-green-600">Top 25%</th>
                        <th className="text-center p-3 font-medium text-foreground">Median</th>
                        <th className="text-center p-3 font-medium text-orange-600">Bottom 25%</th>
                      </tr>
                    </thead>
                    <tbody>
                      {category.metrics.map((metric, i) => (
                        <tr key={i} className="border-t border-border">
                          <td className="p-3 text-muted-foreground">{metric.name}</td>
                          <td className="p-3 text-center font-medium text-green-600">{metric.top25}</td>
                          <td className="p-3 text-center font-medium">{metric.median}</td>
                          <td className="p-3 text-center font-medium text-orange-600">{metric.bottom25}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Email Capture */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-6">
          {!subscribed ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-r from-primary to-accent rounded-2xl p-8 text-white"
            >
              <div className="flex flex-col md:flex-row md:items-center gap-6">
                <div className="flex-1">
                  <h2 className="font-display text-2xl mb-2">Get the Full Report</h2>
                  <p className="text-white/80">
                    Subscribe to receive the complete benchmark report with detailed analysis, 
                    methodology notes, and quarterly updates.
                  </p>
                </div>
                <form onSubmit={handleSubscribe} className="flex gap-3">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="px-4 py-3 rounded-xl bg-white/10 border border-white/20 text-white placeholder-white/60 w-64"
                    required
                  />
                  <button type="submit" className="px-6 py-3 bg-white text-primary font-semibold rounded-xl hover:bg-white/90 flex items-center gap-2">
                    <Download weight="bold" className="w-5 h-5" />
                    Download
                  </button>
                </form>
              </div>
            </motion.div>
          ) : (
            <div className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center">
              <CheckCircle weight="fill" className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <h2 className="font-display text-xl text-foreground mb-2">Report Sent!</h2>
              <p className="text-muted-foreground">Check your inbox for the full benchmark report.</p>
            </div>
          )}
        </div>
      </section>

      {/* Past Reports */}
      <section className="py-12 bg-muted/30">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="font-display text-xl text-foreground mb-6">Past Reports</h2>
          <div className="space-y-3">
            {pastReports.map((report, idx) => (
              <div key={idx} className="bg-white rounded-xl border border-border p-4 flex items-center justify-between hover:shadow-md transition-shadow">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                    <ChartBar weight="duotone" className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium text-foreground">{report.quarter}: {report.title}</h3>
                    <p className="text-sm text-muted-foreground">{report.date}</p>
                  </div>
                </div>
                <button className="btn-secondary text-sm flex items-center gap-2">
                  <Download className="w-4 h-4" />
                  Download
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Sparkle weight="fill" className="w-10 h-10 text-accent mx-auto mb-4" />
          <h2 className="font-display text-2xl text-foreground mb-3">
            Want Personalized Benchmarking?
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto mb-6">
            We can provide a confidential comparison of your organization's metrics against 
            industry benchmarks with actionable improvement recommendations.
          </p>
          <Link to="/schedule-demo" className="btn-primary inline-flex items-center gap-2">
            Request Custom Analysis
            <ArrowRight weight="bold" className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default BenchmarkReportsPage;
